import { ArrowDown, ArrowUp, Bookmark, Bug, CheckSquare, ChevronsDown, ChevronsUp, ListChecks, Minus, Square, Timer, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Avatar, Badge, Select } from '@/components/ui';
import { useUserMap } from '@/lib/queries';
import type { User } from '@/lib/types';
import { cn } from '@/lib/utils';

const TYPE_ICONS: Record<string, typeof Bug> = { epic: Zap, story: Bookmark, task: CheckSquare, bug: Bug, subtask: ListChecks, quick_task: Timer };

export function TypeIcon({ type, className }: { type: { key: string; color?: string; name?: string }; className?: string }) {
  const Icon = TYPE_ICONS[type.key] ?? Square;
  return (
    <span title={type.name ?? type.key} className={cn('inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] text-white', className)} style={{ background: type.color ?? '#64748b' }}>
      <Icon className="h-2.5 w-2.5" strokeWidth={2.5} />
    </span>
  );
}

const STATUS_STYLES: Record<string, string> = {
  triage: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  todo: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  done: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
};

export function StatusBadge({ status, compact }: { status: { name: string; category: string }; compact?: boolean }) {
  return <span className={cn('inline-flex items-center rounded px-1.5 font-semibold uppercase tracking-wide', compact ? 'py-0 text-[9px]' : 'py-0.5 text-[10px]', STATUS_STYLES[status.category])}>{status.name}</span>;
}

const PRIORITY: Record<string, { icon: typeof ArrowUp; color: string; label: string }> = {
  highest: { icon: ChevronsUp, color: 'text-red-600', label: 'Highest' },
  high: { icon: ArrowUp, color: 'text-orange-500', label: 'High' },
  medium: { icon: Minus, color: 'text-amber-500', label: 'Medium' },
  low: { icon: ArrowDown, color: 'text-blue-500', label: 'Low' },
  lowest: { icon: ChevronsDown, color: 'text-slate-400', label: 'Lowest' },
};

export function PriorityIcon({ priority, withLabel }: { priority: string; withLabel?: boolean }) {
  const p = PRIORITY[priority] ?? PRIORITY.medium;
  const Icon = p.icon;
  return (
    <span title={`${p.label} priority`} className="inline-flex items-center gap-1 text-xs">
      <Icon className={cn('h-3.5 w-3.5', p.color)} strokeWidth={2.5} />
      {withLabel && p.label}
    </span>
  );
}

export function UserChip({ userId, size = 20, showName = true }: { userId: string | null | undefined; size?: number; showName?: boolean }) {
  const users = useUserMap();
  const u = userId ? users.get(userId) : null;
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <Avatar name={u?.displayName} url={u?.avatarUrl} size={size} />
      {showName && <span className={cn('truncate text-sm', !u && 'text-subtle')}>{u?.displayName ?? 'Unassigned'}</span>}
    </span>
  );
}

export function UserPicker({ users, value, onChange, placeholder = 'Unassigned', allowEmpty = true, disabled }: { users: User[] | undefined; value: string | null; onChange: (v: string | null) => void; placeholder?: string; allowEmpty?: boolean; disabled?: boolean }) {
  return (
    <Select value={value ?? ''} disabled={disabled} onChange={(e) => onChange(e.target.value || null)}>
      {allowEmpty && <option value="">{placeholder}</option>}
      {(users ?? []).map((u) => (
        <option key={u.id} value={u.id}>
          {u.displayName}
        </option>
      ))}
    </Select>
  );
}

export function ItemKeyLink({ itemKey, className }: { itemKey: string; className?: string }) {
  return (
    <Link to={`/items/${itemKey}`} className={cn('font-medium text-subtle hover:text-brand hover:underline', className)}>
      {itemKey}
    </Link>
  );
}

export function SeverityBadge({ severity }: { severity: string | null }) {
  if (!severity) return null;
  const color = { critical: '#dc2626', major: '#ea580c', minor: '#ca8a04', trivial: '#64748b' }[severity] ?? '#64748b';
  return <Badge color={color}>{severity}</Badge>;
}
